const TS = window.ThreeSheep;
const reduced = matchMedia('(prefers-reduced-motion: reduce)');
const connection = navigator.connection;
let paused = false;
let threePromise;
const scenes = new Map();
const pending = new Map();
const limited = () => reduced.matches || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '') || (navigator.deviceMemory && navigator.deviceMemory <= 4) || (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4);
const loadThree = () => threePromise ||= import(TS.config.threeUrl);
async function mount(root) {
  if (!TS || scenes.has(root) || limited() || paused) return;
  const abort = new AbortController();
  const holder = { dispose: () => { abort.abort(); scenes.delete(root); } };
  scenes.set(root,holder);
  const target = root.querySelector('[data-parallax]');
  const canvasHost = root.querySelector('[data-ambient]');
  let renderer, camera, scene, points, rings, geometry, material;
  let frame = 0, visible = false, last = 0, progress = 0, mouseX = 0, mouseY = 0, currentX = 0, currentY = 0;
  const disposable = [];
  if (TS.config.three && canvasHost) {
    try {
      const THREE = await loadThree();
      if (abort.signal.aborted || !root.isConnected) return;
      renderer = new THREE.WebGLRenderer({alpha:true,antialias:false,powerPreference:'low-power'});
      renderer.setPixelRatio(Math.min(devicePixelRatio,1.5));
      renderer.setClearColor(0,0);
      canvasHost.appendChild(renderer.domElement);
      scene = new THREE.Scene(); camera = new THREE.PerspectiveCamera(35,1,.1,80); camera.position.z = 11;
      geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(550*3);
      for (let i=0;i<550;i++) { const angle=i*2.399963, radius=1.8+Math.sqrt(i/550)*4.3; positions[i*3]=Math.cos(angle)*radius; positions[i*3+1]=Math.sin(angle)*radius*.6; positions[i*3+2]=Math.sin(i*.51)*1.8; }
      geometry.setAttribute('position',new THREE.BufferAttribute(positions,3));
      material = new THREE.PointsMaterial({color:0x91998d,size:.018,transparent:true,opacity:.35,depthWrite:false});
      points = new THREE.Points(geometry,material); scene.add(points); disposable.push(geometry,material);
      rings = new THREE.Group(); scene.add(rings);
      for (let i=0;i<3;i++) {
        const geom=new THREE.TorusGeometry(2.25+i*.42,.004,4,120);
        const mat=new THREE.MeshBasicMaterial({color:0x8a9882,transparent:true,opacity:.12});
        const ring=new THREE.Mesh(geom,mat); ring.rotation.x=.65+i*.34; ring.rotation.y=.3+i*.13; rings.add(ring); disposable.push(geom,mat);
      }
      rings.position.x=2.4;
      renderer.domElement.addEventListener('webglcontextlost',event => { event.preventDefault(); dispose(); },{signal:abort.signal});
    } catch {
      renderer?.dispose(); renderer?.domElement.remove(); renderer = null;
    }
  }
  if (abort.signal.aborted) { renderer?.dispose(); return; }
  const resize = () => {
    if (!renderer) return;
    const width = root.clientWidth, height = root.clientHeight;
    renderer.setSize(width,height,false); camera.aspect=width/height; camera.updateProjectionMatrix();
  };
  function animate(time) {
    if (abort.signal.aborted || !visible || document.hidden || paused || limited()) { frame=0; return; }
    frame=requestAnimationFrame(animate);
    if (time-last < 32) return;
    const delta=Math.min((time-last)/1000,.06); last=time;
    const bounds=root.getBoundingClientRect();
    const desired=Math.max(-.2,Math.min(1.2,-bounds.top/bounds.height));
    const easing=1-Math.exp(-6*delta);
    progress+=(desired-progress)*easing; currentX+=(mouseX-currentX)*easing; currentY+=(mouseY-currentY)*easing;
    if(target) target.style.transform=`translate3d(${currentX*7}px,${-progress*35+currentY*5}px,0) rotate(${currentX*.7}deg)`;
    if(renderer){points.rotation.z=time*.000013+progress*.18;points.rotation.y=currentX*.05; rings.rotation.z=progress*.42+time*.000018; rings.rotation.y=currentX*.08;camera.position.y=progress*.5; renderer.render(scene,camera);}
  }
  const start = () => { if (!frame && visible && !document.hidden && !paused && !limited()) { last=performance.now();frame=requestAnimationFrame(animate); } };
  const stop = () => { cancelAnimationFrame(frame);frame=0; };
  const observer=new IntersectionObserver(entries=>{visible=entries[0].isIntersecting; if(visible)start();else stop();},{threshold:0}); observer.observe(root);
  const ro = new ResizeObserver(resize);ro.observe(root);
  root.addEventListener('pointermove',event=>{const bounds=root.getBoundingClientRect();mouseX=(event.clientX-bounds.left)/bounds.width-.5;mouseY=(event.clientY-bounds.top)/bounds.height-.5;},{passive:true,signal:abort.signal});
  root.addEventListener('pointerleave',()=>{mouseX=0;mouseY=0;},{signal:abort.signal});
  document.addEventListener('visibilitychange',()=>{if(document.hidden)stop();else start();},{signal:abort.signal});
  function dispose(){stop();abort.abort();observer.disconnect();ro.disconnect();disposable.forEach(object=>object.dispose());renderer?.dispose();renderer?.domElement.remove();if(target)target.style.transform='';scenes.delete(root);}
  holder.dispose=dispose; holder.start=start; holder.stop=stop;
  resize();
}
function init(scope=document){
 if(!('IntersectionObserver' in window)||!('ResizeObserver' in window)||limited()||paused)return;
 scope.querySelectorAll('[data-motion-scene]').forEach(root=>{
  if(scenes.has(root)||pending.has(root))return;
  const observer=new IntersectionObserver(entries=>{
   if(!entries[0].isIntersecting)return;
   observer.disconnect();pending.delete(root);
   const start=()=>{if(root.isConnected&&!limited()&&!paused)mount(root);};
   if('requestIdleCallback'in window)window.requestIdleCallback(start,{timeout:1500});else setTimeout(start,150);
  },{rootMargin:'100px'});
  pending.set(root,observer);observer.observe(root);
 });
}
function refresh(){pending.forEach(observer=>observer.disconnect());pending.clear();scenes.forEach(item=>item.dispose());if(!limited()&&!paused)init();}
if(TS){
 if(document.readyState==='complete')init();else addEventListener('load',()=>init(),{once:true});
 reduced.addEventListener('change',refresh);connection?.addEventListener('change',refresh);
 document.addEventListener('click',event=>{
  const button=event.target.closest('[data-motion-toggle]');if(!button)return;
  paused=!paused;document.documentElement.classList.toggle('motion-paused',paused);
  document.querySelectorAll('[data-motion-toggle]').forEach(node=>{node.textContent=paused?TS.config.strings.motionResume:TS.config.strings.motionPause;node.setAttribute('aria-pressed',String(paused));});refresh();
 });
 document.addEventListener('three-sheep:section_load',event=>init(event.detail.root));
 document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('[data-motion-scene]').forEach(root=>{scenes.get(root)?.dispose();pending.get(root)?.disconnect();pending.delete(root);}));
}
